import type {
  InputData,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import {
  InputField,
  InputGroup,
  Text,
  useInputGroupState,
} from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import type React from "react";
import { useEffect, useState } from "react";
import { isRequired } from "../../Forms/InputForm/utils";
import {
  StyledInputLabel,
  StyledInputSubLabel,
  StyledReactInputFieldContainer,
} from "./styles";
import { SanitizedHTML } from "../../../../utils/sanitizedHtml";
import { translateIfPresent } from "../../../../locales/i18n";

export const TextEditor: React.FC<{
  description: InputDescription;
  input: InputData;
  onChange: (input: InputData) => void;
  hasError?: boolean;
}> = ({ description, input, onChange, hasError }) => {
  const inputValue = input.value?.values?.[0].value;
  const [value, setValue] = useState<string>("");

  const { state, onBlur, onFocus } = useInputGroupState();

  useEffect(() => {
    if (inputValue?.oneofKind === "string") {
      setValue(inputValue.string);
    }
  }, [inputValue]);

  if (description.editor?.editor.oneofKind !== "text") return null;
  if (inputValue?.oneofKind !== "string") return null;

  return (
    <StyledReactInputFieldContainer>
      {description.label !== "" && (
        <>
          <StyledInputLabel>
            {t(`caption.${description.label}`, description.label)}
          </StyledInputLabel>
          {isRequired(description.display) && (
            <Text size="s" weight="semibold" color="error">
              *
            </Text>
          )}
        </>
      )}
      {description.subLabel !== "" && (
        <StyledInputSubLabel>
          <SanitizedHTML
            html={translateIfPresent(
              `caption.${description.subLabel}`,
              description.subLabel,
            )}
            options={{
              allowedTags: ["span", "p", "b", "a"],
              allowedAttributes: {
                a: ["href", "target"],
                span: ["style"],
              },
            }}
          />
        </StyledInputSubLabel>
      )}
      <InputGroup state={state}>
        <InputField
          placeholder={t("automation-form.text-editor-type-input")}
          type="text"
          id={description.inputId}
          value={value}
          className={hasError ? "error" : ""}
          onChange={({ target: { value } }) => setValue(value)}
          onFocus={onFocus}
          onBlur={({ target: { value } }) => {
            onBlur();
            if (value !== inputValue.string) {
              onChange({
                ...input,
                value: {
                  values: [
                    {
                      value: { oneofKind: "string", string: value },
                      label: "",
                      icon: "",
                    },
                  ],
                  metadata: {},
                },
              });
            }
          }}
        />
      </InputGroup>
    </StyledReactInputFieldContainer>
  );
};
